import { DialogComponent, DialogCloseEvent } from '@theme/dialog';

/**
 * A custom element that manages a modal section with hash-based triggering.
 *
 * Features:
 * - Hash-based trigger system (#trigger-id)
 * - Click-based trigger via [data-modal-trigger] attribute
 * - Auto-open on page load (optional)
 * - Show once per session (optional)
 * - Focus restoration to trigger element
 * - Proper ARIA attributes for accessibility
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLButtonElement} closeButton - The close button element.
 *
 * @extends DialogComponent
 */
export class ModalSection extends DialogComponent {
  requiredRefs = ['dialog', 'closeButton'];

  /** @type {HTMLElement | null} */
  #triggerElement = null;

  /** @type {string} */
  #sessionKey = '';

  connectedCallback() {
    super.connectedCallback();

    const triggerId = this.dataset.triggerId;
    if (triggerId) {
      this.#sessionKey = `modal-shown-${triggerId}`;
    }

    // Listen for hash changes
    window.addEventListener('hashchange', this.#handleHashChange);

    // Listen for clicks on trigger elements (both data-modal-trigger and href="#trigger-id")
    document.addEventListener('click', this.#handleTriggerClick);

    // Listen for dialog close events to restore focus
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);

    // Check initial hash on load
    this.#checkInitialHash();

    // Handle auto-open
    if (this.hasAttribute('data-auto-open')) {
      this.#handleAutoOpen();
    }

    // Handle exit-intent trigger
    if (this.hasAttribute('data-exit-intent')) {
      this.#handleExitIntent();
    }

    // Handle editor preview mode
    if (this.hasAttribute('data-editor-preview')) {
      this.#handleEditorPreview();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('hashchange', this.#handleHashChange);
    document.removeEventListener('click', this.#handleTriggerClick);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Handles the dialog close event to restore focus.
   */
  #handleDialogClose = () => {
    // Restore focus to the trigger element
    if (this.#triggerElement && document.contains(this.#triggerElement)) {
      this.#triggerElement.focus();
      this.#triggerElement = null;
    }
  };

  /**
   * Checks if the modal should be shown based on initial page hash.
   */
  #checkInitialHash = () => {
    const hash = window.location.hash.slice(1); // Remove the #
    const triggerId = this.dataset.triggerId;

    if (hash && hash === triggerId) {
      // Small delay to ensure page is ready
      requestAnimationFrame(() => {
        this.showDialog();
        // Clear the hash to prevent re-opening on refresh
        history.replaceState(null, '', window.location.pathname + window.location.search);
      });
    }
  };

  /**
   * Handles hash changes to open the modal.
   *
   * @param {HashChangeEvent} _event - The hash change event.
   */
  #handleHashChange = (_event) => {
    const hash = window.location.hash.slice(1);
    const triggerId = this.dataset.triggerId;

    if (hash && hash === triggerId) {
      this.showDialog();
      // Clear the hash to prevent re-opening on back navigation
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  };

  /**
   * Handles clicks on elements that trigger this modal.
   * Supports both data-modal-trigger attribute and href="#trigger-id" anchors.
   *
   * @param {MouseEvent} event - The click event.
   */
  #handleTriggerClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const triggerId = this.dataset.triggerId;

    // Check for data-modal-trigger attribute
    const dataTrigger = target.closest('[data-modal-trigger]');
    if (dataTrigger) {
      const targetId = dataTrigger.getAttribute('data-modal-trigger');
      if (targetId === triggerId) {
        event.preventDefault();
        this.#triggerElement = /** @type {HTMLElement} */ (dataTrigger);
        this.showDialog();
        return;
      }
    }

    // Check for anchor with href="#trigger-id"
    const anchor = target.closest('a[href]');
    if (anchor) {
      const href = anchor.getAttribute('href');
      if (href === `#${triggerId}`) {
        event.preventDefault();
        this.#triggerElement = /** @type {HTMLElement} */ (anchor);
        this.showDialog();
      }
    }
  };

  /**
   * Handles editor preview mode - opens modal immediately for theme editor.
   */
  #handleEditorPreview = () => {
    // Small delay to ensure component is fully initialized
    requestAnimationFrame(() => {
      this.showDialog();
    });
  };

  /**
   * Handles auto-open functionality with optional delay.
   */
  #handleAutoOpen = () => {
    if (this.#shouldSkipAutoTrigger()) return;

    const delaySeconds = parseInt(this.dataset.autoOpenDelay || '0', 10);
    const delayMs = delaySeconds * 1000;

    if (delayMs > 0) {
      setTimeout(() => {
        this.#showAndMarkAsShown();
      }, delayMs);
    } else {
      // Small delay to ensure page is fully loaded
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.#showAndMarkAsShown();
        });
      });
    }
  };

  /**
   * Handles exit-intent trigger - opens modal when mouse leaves the viewport.
   */
  #handleExitIntent = () => {
    if (this.#shouldSkipAutoTrigger()) return;

    /** @param {MouseEvent} event */
    const handleMouseLeave = (event) => {
      // Only trigger when mouse leaves from the top of the viewport (like closing a tab)
      if (event.clientY <= 0) {
        document.removeEventListener('mouseleave', handleMouseLeave);
        this.#showAndMarkAsShown();
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
  };

  /**
   * Checks if auto-trigger should be skipped (already shown this session).
   *
   * @returns {boolean} True if auto-trigger should be skipped.
   */
  #shouldSkipAutoTrigger = () => {
    const showOnce = this.hasAttribute('data-show-once');

    if (showOnce) {
      try {
        const hasShown = sessionStorage.getItem(this.#sessionKey);
        if (hasShown) return true;
      } catch {
        // Session storage not available, continue
      }
    }

    return false;
  };

  /**
   * Shows the dialog and marks it as shown in session storage if needed.
   */
  #showAndMarkAsShown = () => {
    this.showDialog();

    const showOnce = this.hasAttribute('data-show-once');
    if (showOnce) {
      try {
        sessionStorage.setItem(this.#sessionKey, 'true');
      } catch {
        // Session storage not available, ignore
      }
    }
  };

  /**
   * Shows the dialog and manages focus.
   * @override
   */
  showDialog() {
    super.showDialog();

    // Focus the close button when dialog opens for accessibility
    requestAnimationFrame(() => {
      const { closeButton } = this.refs;
      if (closeButton && closeButton instanceof HTMLElement) {
        closeButton.focus();
      }
    });
  }

  /**
   * Opens the modal programmatically from external scripts.
   *
   * @param {HTMLElement} [triggerElement] - Optional element to restore focus to on close.
   */
  open(triggerElement) {
    if (triggerElement) {
      this.#triggerElement = triggerElement;
    }
    this.showDialog();
  }

  /**
   * Closes the modal programmatically from external scripts.
   */
  close() {
    this.closeDialog();
  }

  /**
   * Checks if the modal is currently open.
   *
   * @returns {boolean} True if the modal is open.
   */
  get isOpen() {
    const { dialog } = this.refs;
    return dialog instanceof HTMLDialogElement ? dialog.open : false;
  }
}

if (!customElements.get('modal-section')) {
  customElements.define('modal-section', ModalSection);
}

/**
 * Utility function to open a modal by its trigger ID.
 *
 * @param {string} triggerId - The trigger ID of the modal to open.
 * @param {HTMLElement} [triggerElement] - Optional element to restore focus to on close.
 * @returns {boolean} True if the modal was found and opened.
 */
export function openModal(triggerId, triggerElement) {
  const modal = /** @type {ModalSection | null} */ (
    document.querySelector(`modal-section[data-trigger-id="${triggerId}"]`)
  );

  if (modal instanceof ModalSection) {
    modal.open(triggerElement);
    return true;
  }

  return false;
}

/**
 * Utility function to close a modal by its trigger ID.
 *
 * @param {string} triggerId - The trigger ID of the modal to close.
 * @returns {boolean} True if the modal was found and closed.
 */
export function closeModal(triggerId) {
  const modal = /** @type {ModalSection | null} */ (
    document.querySelector(`modal-section[data-trigger-id="${triggerId}"]`)
  );

  if (modal instanceof ModalSection) {
    modal.close();
    return true;
  }

  return false;
}
